import { useState, useCallback, useEffect } from "react";
import { useLocalization } from "@fluent/react";
import { Row, Button, TextInput } from "@narmi/design_system";
import { Dialog } from "cerulean";

type SaveMappingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  existingNames: string[];
  loading: boolean;
};

const SaveMappingModal = ({
  isOpen,
  onClose,
  onSave,
  existingNames,
  loading,
}: SaveMappingModalProps) => {
  const { l10n } = useLocalization();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setError(null);
    }
  }, [isOpen]);

  const validate = useCallback(
    (value: string): string | null => {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return l10n.getString("column-mapping-save-required-error");
      }
      if (
        existingNames.some(
          (n) => n.toLowerCase() === trimmedValue.toLowerCase()
        )
      ) {
        return l10n.getString("column-mapping-save-unique-error");
      }
      return null;
    },
    [existingNames, l10n]
  );

  const handleSave = useCallback(() => {
    const validationError = validate(name);
    if (validationError) {
      setError(validationError);
      return;
    }
    onSave(name.trim());
  }, [name, validate, onSave]);

  const handleClose = useCallback(() => {
    setName("");
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog
      isOpen={isOpen}
      title={l10n.getString("column-mapping-save-modal-title")}
      onUserDismiss={handleClose}
      footer={
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              label={l10n.getString("button-back")}
              onClick={handleClose}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("column-mapping-save-mapping-button")}
              onClick={handleSave}
              disabled={loading}
            />
          </Row.Item>
        </Row>
      }
    >
      <div className="margin--bottom--m">
        <span className="fontSize--s fontColor--secondary">
          {l10n.getString("column-mapping-save-modal-description")}
        </span>
      </div>
      <TextInput
        label={l10n.getString("column-mapping-mapping-name-label")}
        value={name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setName(e.target.value);
          if (error) {
            setError(null);
          }
        }}
        error={error}
      />
    </Dialog>
  );
};

export default SaveMappingModal;
